import type {
  CollectState,
  ComposeState,
  QuoteSnapshot,
} from '../components/smart/wizard/types';
import type { CountryOption } from '../components/smart/steps/RouteAmountStep';
import type { FundingMethod, PayoutMethod } from '../components/smart/utils/catalog';

/** Partner chrome for splash / shell (no real logo URL → text brand). */
export const smartMockPartner = {
  name: 'Lola Send',
  headerName: 'Lola Send',
  linkId: 'gallery-smart-link',
};

export const smartMockOrigins: CountryOption[] = [
  { code: 'US', name: 'United States', flag: 'us', currency: 'USD' },
  { code: 'MX', name: 'Mexico', flag: 'mx', currency: 'MXN' },
];

export const smartMockDestinations: CountryOption[] = [
  { code: 'MX', name: 'Mexico', flag: 'mx', currency: 'MXN' },
  { code: 'GT', name: 'Guatemala', flag: 'gt', currency: 'GTQ' },
  { code: 'CO', name: 'Colombia', flag: 'co', currency: 'COP' },
];

export const smartMockFundingMethods: FundingMethod[] = [
  'DEBIT_CARD',
  'CREDIT_CARD',
  'ACH',
  'CASH_DEPOSIT',
];

export const smartMockPayoutMethods: PayoutMethod[] = [
  'CASH_PICKUP',
  'BANK_ACCOUNT',
  'PUSH_TO_CARD',
  'MOBILE_WALLET',
];

export const smartMockPayoutHints: Partial<Record<PayoutMethod, string>> = {
  CASH_PICKUP: 'Banrural, Western Union',
  BANK_ACCOUNT: 'BBVA, Banorte',
};

export const smartMockQuote: QuoteSnapshot = {
  quoteId: 'gallery-quote-smart',
  exchangeRate: 17.4521,
  totalFee: 4.99,
  sendAmount: 62.35,
  receiveAmount: 1000,
  sendCurrency: 'USD',
  receiveCurrency: 'MXN',
  expiresAt: new Date(Date.now() + 15 * 60_000).toISOString(),
  estimated: false,
};

/** Collect summary quote for the first-send variant (above the 500 USD threshold). */
export const smartMockFirstSendQuote: QuoteSnapshot = {
  ...smartMockQuote,
  quoteId: 'gallery-quote-smart-first-send',
  sendAmount: 750,
  receiveAmount: 13089.08,
};

/** Fully filled send compose — used by later wizard / collect / done screens. */
export const smartMockCompose: ComposeState = {
  intent: 'send',
  route: {
    fromCountry: 'US',
    toCountry: 'MX',
    amount: '1000',
    direction: 'receive',
  },
  funding: 'DEBIT_CARD',
  payout: 'CASH_PICKUP',
  payoutCatalog: { state: 'JAL', payer: 'BANRURAL' },
};

export const smartMockCollect: CollectState = {
  recipientIdentity: {
    fullName: 'Maria Lopez Garcia',
    canonicalName: {
      firstName: 'Maria',
      middleName: 'Lopez',
      lastName: 'Garcia',
    },
  },
  bookDestinations: [],
  payoutDetails: {
    type: 'CASH_PICKUP',
    stateCode: 'JAL',
    payerId: 'BANRURAL',
  },
  fundingDetails: {
    type: 'CARD',
    source: 'saved',
    paymentMethodId: 'pm_gallery_debit',
    brand: 'visa',
    last4: '4242',
    expiry: '12/28',
    cardholderName: 'Alex Sender',
  },
  selectedCapabilityKey: 'sku_gallery_us_mx_cash',
};

export const smartMockOperations = [
  {
    operationId: 'op_gallery_001',
    status: 'completed',
    fundingStatus: undefined as string | undefined,
    sendAmount: 62.35,
    receiveAmount: 1000,
    sourceCurrency: 'USD',
    destinationCurrency: 'MXN',
    createdAt: new Date(Date.now() - 2 * 60 * 60_000).toISOString(),
  },
  {
    operationId: 'op_gallery_002',
    status: 'pending',
    fundingStatus: 'awaiting_funding',
    sendAmount: 50,
    receiveAmount: 850,
    sourceCurrency: 'USD',
    destinationCurrency: 'MXN',
    createdAt: new Date(Date.now() - 30 * 60_000).toISOString(),
  },
  {
    operationId: 'op_gallery_003',
    status: 'pending',
    fundingStatus: 'barcode_issued',
    sendAmount: 40,
    receiveAmount: 680,
    sourceCurrency: 'USD',
    destinationCurrency: 'MXN',
    createdAt: new Date(Date.now() - 10 * 60_000).toISOString(),
  },
];

export const smartMockRequests = [
  {
    requestId: 'req_gallery_001',
    status: 'pending' as const,
    amount: 500,
    fundRequestUrl: 'https://example.com/fund-request/gallery-demo',
    corridor: {
      sourceCurrency: 'USD',
      destinationCurrency: 'MXN',
    },
    createdAt: new Date(Date.now() - 45 * 60_000).toISOString(),
  },
  {
    requestId: 'req_gallery_002',
    status: 'funded' as const,
    amount: 1200,
    fundRequestUrl: null as string | null,
    corridor: {
      sourceCurrency: 'USD',
      destinationCurrency: 'MXN',
    },
    createdAt: new Date(Date.now() - 24 * 60 * 60_000).toISOString(),
  },
];

export const smartMockMerchants = [
  {
    merchantId: 'cvs',
    name: 'CVS Pharmacy',
    fee: 3.99,
    states: ['CA', 'TX', 'NY', 'FL'],
    logoUrl: null as string | null,
  },
  {
    merchantId: 'walgreens',
    name: 'Walgreens',
    fee: 3.99,
    states: ['CA', 'TX', 'IL'],
    logoUrl: null as string | null,
  },
  {
    merchantId: '7eleven',
    name: '7-Eleven',
    fee: 4.5,
    states: ['CA', 'NY', 'FL', 'TX', 'AZ'],
    logoUrl: null as string | null,
  },
  {
    merchantId: 'walmart',
    name: 'Walmart',
    fee: 0,
    states: ['CA', 'TX', 'FL', 'GA'],
    logoUrl: null as string | null,
  },
];

export const smartMockBarcode = {
  data: '4912345678901234',
  ref: 'REF-GALLERY-88421',
  amount: 50,
  currency: 'USD',
  merchantName: 'CVS Pharmacy',
  expiresAt: new Date(Date.now() + 45 * 60_000).toISOString(),
  status: 'issued' as const,
};

export const smartMockShareLink =
  'https://example.com/fund-request/gallery-smart-share';

export const smartMockOperationId = 'op_gallery_send_done';

/** Recent recipients for the collect name step (static). */
export const smartMockRecipients = [
  {
    recipientKey: 'rec_maria',
    fullName: 'Maria Lopez Garcia',
  },
  {
    recipientKey: 'rec_juan',
    fullName: 'Juan Perez',
  },
];

/**
 * OCR fields for the CIP identity-review gallery screen (mirrors scan-id
 * `data` bag). Keys match live `IdentityReviewScreen` REVIEW_FIELDS.
 */
export const smartMockCipScanData: Record<string, string> = {
  FullName: 'Maria Elena Lopez Garcia',
  DocumentType: 'Passport',
  IDNumber: 'G12345678',
  DoB: '1990-04-12',
  ExpDate: '2030-04-12',
  Country: 'MX',
  Address: 'Av. Reforma 222, CDMX 06600',
};

/** Pre-filled address form for the CIP address gallery screen. */
export const smartMockCipAddress = {
  street1: 'Av. Reforma 222',
  street2: 'Piso 4',
  city: 'Ciudad de México',
  state: 'CDMX',
  postalCode: '06600',
  countryCode: 'MX',
};
